import type { Habit } from '../types';

export function isHabitDue(habit: Habit, date: string): boolean {
  const checkDate = new Date(date);
  const { schedule } = habit;

  switch (schedule.interval) {
    case 'daily':
      return true;

    case 'weekly': {
      if (!schedule.days) return false;
      return schedule.days.includes(checkDate.getDay());
    }

    case 'monthly': {
      if (!schedule.monthDays) return false;
      return schedule.monthDays.includes(checkDate.getDate());
    }

    case 'yearly': {
      if (!schedule.yearDates) return false;
      const monthDay = date.slice(5); // Get MM-DD
      return schedule.yearDates.some(d => d.slice(5) === monthDay);
    }

    default:
      return false;
  }
}

export function calculateStreak(habit: Habit, currentDate: string): number {
  let streak = 0;
  let missedDays = 0;
  const date = new Date(currentDate);
  const completedSet = new Set(habit.completedDates);

  // Count backwards until we find a break in the streak
  while (true) {
    const dateStr = date.toISOString().split('T')[0];
    
    // Check if this date should be counted
    if (isHabitDue(habit, dateStr)) {
      if (completedSet.has(dateStr)) {
        streak++;
        missedDays = 0; // Reset missed days when we find a completion
      } else {
        missedDays++;
        // Allow one missed day before breaking streak
        if (missedDays > 1) break;
      }
    }

    // Move to previous day
    date.setDate(date.getDate() - 1);
  }

  return streak;
}

// Helper function to get dates for a time frame
function getDatesForTimeFrame(timeFrame: 'today' | 'week' | 'month' | 'year'): string[] {
  const dates: string[] = [];
  const today = new Date();
  let startDate = new Date(today);
  let endDate = new Date(today);

  switch (timeFrame) {
    case 'today':
      dates.push(today.toISOString().split('T')[0]);
      break;

    case 'week':
      // Get dates for the current week
      startDate.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
      endDate.setDate(startDate.getDate() + 6); // End of week (Saturday)
      break;

    case 'month':
      // Get dates for the current month
      startDate.setDate(1); // Start of month
      endDate.setMonth(today.getMonth() + 1, 0); // End of month
      break;

    case 'year':
      // Get dates for the current year
      startDate = new Date(today.getFullYear(), 0, 1); // Start of year
      endDate = new Date(today.getFullYear(), 11, 31); // End of year
      break;
  }

  // If not 'today', generate array of dates
  if (timeFrame !== 'today') {
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  return dates;
}